/* eslint-disable max-lines-per-function */
import { RangeValue } from "@evergis/ui";
import { SimplePolylineSymbol } from "@evergis/react";
import { SimplePolylineSymbolDc } from "@evergis/api";

import { Style } from "../../../ducks/style/style";
import { Classification, ClassificationClass } from "../../../interfaces/classification";
import {
  ClassifyAble,
  isParameterValueSymbol,
  isSymbolWithOffset,
  ParameterByAttribute,
  ParameterValue,
  ParameterValueWithSymbol,
  SimpleOffset,
  StyleSymbolType,
  StrokeBrushType,
  FillBrushType,
  Unclassify,
  isClusterSymbol,
  isClusterFillColor,
  isCombinedPolylineSymbol,
  CombinedPolylineSymbol,
  ClusterSymbol,
} from "../../../interfaces/style";
import { addOffsetClassification, createOffsetClassification, getOffsetParameterValue } from "./classifyOffset";
import { getParameterValue, isStrokeParameter, isFillParameter } from "./getFromClassified";
import { getExtractedSymbol, getStyleLabel, updateSpecSymbol } from "../../../utils/style";
import { isSizeClassification } from "./classificationMapping";
import { isLabelParameter } from "../../../utils/isLabelParameter";
import { updateSymbolParameter } from "./updateSymbolParameter";
import { transparentizeColor } from "../../../utils/color";
import { formatPolyline } from "./formatPolyline";
import { ClassificationCondition } from "../condition/ClassificationCondition";

const createCondition = (value: ClassificationClass, index: number) =>
  ClassificationCondition.from(value, index === 0 ? { fromInclusive: true, toInclusive: true } : undefined).toString();

function classifyValuesByAttribute<Value extends ParameterValue>(
  values: ClassificationClass[],
  defaultTitle: string,
  currentParameterValue: ClassifyAble<Value>,
): ParameterByAttribute<Value> {
  const classifiedValues = values.map((value, index) => ({
    condition: createCondition(value, index),
    // TODO make classification class generic
    value: value.parameterValue as Value,
    title: value.title,
  }));

  return {
    type: "byAttribute",
    defaultValue: getParameterValue(currentParameterValue),
    title: defaultTitle || null,
    values: classifiedValues,
  };
}

export const unClassify = <V extends StyleSymbolType | StrokeBrushType | FillBrushType>(value: V): Unclassify<V> =>
  Object.entries(value).reduce((flatSymbol, [parameter, parameterValue]) => {
    return {
      ...flatSymbol,
      [parameter]:
        isStrokeParameter(parameter, parameterValue) || isFillParameter(parameter, parameterValue)
          ? unClassify(parameterValue)
          : getParameterValue(parameterValue),
    };
  }, {} as Unclassify<V>);

function classifySymbolByAttribute(
  values: ClassificationClass[],
  style: Style,
  minMaxResolution?: RangeValue,
): Style[] {
  return values.map((value, index) => ({
    ...style,
    minResolution: minMaxResolution ? minMaxResolution[0] || 0 : 0,
    maxResolution: minMaxResolution ? minMaxResolution[1] || 0 : 0,
    condition: createCondition(value, index),
    // TODO make classification class generic
    symbol: unClassify(value.parameterValue as StyleSymbolType),
    title: value.title,
    children: null,
  }));
}

const classifyOffsetByAttribute = (
  offset: [ClassificationClass[], ClassificationClass[]],
  title: string,
  defaultValue: SimpleOffset,
) => [
  classifyValuesByAttribute(offset[0], title, defaultValue[0]),
  classifyValuesByAttribute(offset[1], title, defaultValue[1]),
];

const addSymbolClassification = (style: Style, symbol: StyleSymbolType, title: string, children: Style[]) => ({
  ...style,
  symbol,
  title: title || null,
  children,
});

export const classifyStyle =
  (defaultValue: ParameterValueWithSymbol, defaultTitle?: string, minMaxResolution?: RangeValue, valueIndex?: number) =>
  (style: Style, classification: Classification): Style => {
    const label = style.label || getStyleLabel(style.symbol);

    const hasLabel = !!label;
    const { parameter, values } = classification;
    const isValuesNotEmpty = values.length > 0;

    if (isParameterValueSymbol(defaultValue)) {
      const styleChildren = isValuesNotEmpty ? classifySymbolByAttribute(values, style, minMaxResolution) : null;

      return addSymbolClassification(style, defaultValue, defaultTitle, styleChildren);
    }

    const parameterClassifiedByAttribute = isValuesNotEmpty
      ? classifyValuesByAttribute(values, defaultTitle, defaultValue)
      : defaultValue;

    if (isLabelParameter(parameter)) {
      const classifiedLabel = updateSymbolParameter(label, parameter, parameterClassifiedByAttribute);

      return {
        ...style,
        label: classifiedLabel,
      };
    }

    const isCluster = isClusterFillColor(style.symbol, parameter);
    const extracted = !isCluster && getExtractedSymbol(style.symbol, true);

    let classifiedSymbol = updateSymbolParameter(
      isCluster
        ? style.symbol
        : isCombinedPolylineSymbol(extracted)
        ? ((extracted as CombinedPolylineSymbol).values[valueIndex] as SimplePolylineSymbol)
        : extracted,
      parameter,
      parameterClassifiedByAttribute,
    );

    if (
      hasLabel &&
      typeof defaultValue === "number" &&
      isSizeClassification(parameter) &&
      isSymbolWithOffset(classifiedSymbol)
    ) {
      const defaultOffsetValue = getOffsetParameterValue(classifiedSymbol.offset);
      const { offsetX, offsetY } = createOffsetClassification(values, defaultValue, defaultOffsetValue);
      const [offsetXClassified, offsetYClassified] = classifyOffsetByAttribute(
        [offsetX, offsetY],
        defaultTitle,
        defaultOffsetValue,
      );

      classifiedSymbol = addOffsetClassification(classifiedSymbol, offsetXClassified, offsetYClassified);
    }

    const createClassifiedStrokeColor = (fillColor: ParameterByAttribute<string>) => ({
      ...fillColor,
      defaultValue: transparentizeColor(fillColor.defaultValue, 28),
      values: fillColor.values.map(item => ({ ...item, value: transparentizeColor(item.value, 28) })),
    });

    const formatSymbol = () => {
      if (isClusterSymbol(style.symbol)) {
        return parameter === "fillColor"
          ? ({
              ...classifiedSymbol,
              strokeColor: createClassifiedStrokeColor(
                (classifiedSymbol as ClusterSymbol).fillColor as ParameterByAttribute<string>,
              ),
            } as ClusterSymbol)
          : (updateSpecSymbol(classifiedSymbol, style.symbol) as StyleSymbolType);
      }

      if (isCombinedPolylineSymbol(style.symbol)) {
        const newValues = [...style.symbol.values];

        newValues[valueIndex] = formatPolyline(JSON.parse(JSON.stringify(classifiedSymbol)) as SimplePolylineSymbolDc);

        return { ...style.symbol, values: newValues };
      }

      return classifiedSymbol;
    };

    return {
      ...style,
      symbol: formatSymbol(),
    };
  };
